import { Result1, MilestoneMap1, Objective1 } from '../../../types';
import { DependencyRecord } from '@mm-shared/dependencies';
import { DataNode } from '../types';
import { getMapUrl } from '../../utils/mapUrlUtils';

/**
 * Build dependency DataNodes for a result that has dependencies
 *
 * This follows the same pattern as Map3Relatives.tsx:
 * 1. Create dependencies from result.dependencies (primary source)
 * 2. Use dependencyRecords for additional metadata when available
 * 3. Search all objectives to resolve target result text
 *
 * @param result - The result that may have dependencies
 * @param dependencyRecords - All dependency records from Redux store (for additional metadata)
 * @param allMaps - All milestone maps for building cross-map links
 * @param allObjectives - All objectives to resolve target result text
 * @returns Array of dependency DataNodes
 */
export function buildDependencyDataNodes(
  result: Result1,
  dependencyRecords: DependencyRecord[],
  allMaps: MilestoneMap1[],
  allObjectives: Objective1[] = [],
): DataNode<any>[] {
  // Check if this result has any dependencies
  if (!result.dependencies || result.dependencies.length === 0) {
    return [];
  }

  // Create dependency DataNodes from result.dependencies (primary source)
  return result.dependencies.map((dep, index) => {
    // Try to find matching dependency record for additional metadata
    const matchingRecord = dependencyRecords.find(
      (record) =>
        record.sourceResult.uuid === result.uuid &&
        record.targetResult.uuid === dep.targetResultUuid,
    );

    // Try to resolve target result text by searching through all objectives
    let targetResultText: string = dep.targetResultUuid; // fallback to UUID
    let targetMapTitle: string = 'Unknown Map';
    let targetMapUuid: string = dep.targetMapUuid || 'unknown';

    // Search through all objectives to find the target result
    for (const objective of allObjectives) {
      const targetResult = objective.results.find((r) => r.uuid === dep.targetResultUuid);
      if (targetResult) {
        targetResultText = targetResult.text;
        // Try to find the map that contains this objective
        const targetMap = allMaps.find((map) => map.objectiveUuids.includes(objective.uuid));
        if (targetMap) {
          targetMapTitle = targetMap.title;
          targetMapUuid = targetMap.uuid;
        }
        break;
      }
    }

    // Build dependency data - use record data if available, fallback to resolved info
    let sourceText: string;
    let targetText: string;
    let sourceMap: { uuid: string; text: string };
    let targetMap: { uuid: string; text: string };
    let isDifferentMap: boolean = false;
    let mapUrl: string | null = null;

    if (matchingRecord) {
      // Use rich data from dependency record (preferred for cross-map dependencies)
      sourceText = matchingRecord.sourceResult.text;
      targetText = matchingRecord.targetResult.text;
      sourceMap = matchingRecord.sourceMap;
      targetMap = matchingRecord.targetMap;
      isDifferentMap = sourceMap.uuid !== targetMap.uuid;
    } else {
      // Fallback to resolved info when no dependency record is available
      sourceText = result.text;
      targetText =
        targetResultText !== dep.targetResultUuid
          ? targetResultText
          : `[Target: ${dep.targetResultUuid}]`;
      sourceMap = { uuid: 'unknown', text: 'Current Map' };
      targetMap = { uuid: targetMapUuid, text: targetMapTitle };
      isDifferentMap = !!(targetMapUuid && targetMapUuid !== 'unknown');
    }

    // Build formatted specific info with (A) and (B) labels and proper line breaks
    let specificInfo = `(A) ${sourceText}\ndepends on\n(B) ${targetText}`;

    // Add link if it's a different map
    if (isDifferentMap) {
      const relativeUrl = getMapUrl(targetMap.uuid, allMaps);
      if (relativeUrl) {
        // Convert relative URL to full URL
        const fullUrl = `http://milestonemap.org${relativeUrl}`;
        specificInfo += `\n\nDrill down to view (B) on ${targetMap.text} here:\n${fullUrl}`;
        mapUrl = fullUrl;
      }
    }

    const dependencyNode = {
      id: `${result.uuid}-dependency-${index + 1}`,
      data: {
        dependency: matchingRecord,
        dependencyIndex: index + 1,
        sourceResult: { uuid: result.uuid, text: sourceText },
        targetResult: { uuid: dep.targetResultUuid, text: targetText },
        sourceMap,
        targetMap,
      },
      children: [],
      type: 'Dependency',
      metadata: {
        specificInfo,
        isDifferentMap,
        mapUrl,
        // Add URL in metadata for clickable link support
        url: mapUrl,
      },
    } as DataNode<any>;

    return dependencyNode;
  });
}
